/**
 * Servicio de limpieza automática de caché obsoleta
 */

// Tiempo máximo en minutos para considerar una entrada de caché como obsoleta
const MAX_CACHE_AGE = 24 * 60; // 24 horas

/**
 * Limpia las entradas de caché obsoletas (más antiguas que MAX_CACHE_AGE)
 */
export const cleanupStaleCache = () => {
    try {
        const currentTime = Date.now();
        let itemsRemoved = 0;
        
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('profile_cache_') || key.includes('_profile_data')) {
                try {
                    const cachedItem = localStorage.getItem(key);
                    if (!cachedItem) return;
                    
                    const { timestamp } = JSON.parse(cachedItem);
                    const cacheAge = (currentTime - timestamp) / (1000 * 60); // en minutos
                    
                    if (cacheAge > MAX_CACHE_AGE) {
                        localStorage.removeItem(key);
                        itemsRemoved++;
                    }
                } catch (parseError) {
                    // Si hay error al parsear, simplemente eliminar la entrada
                    localStorage.removeItem(key);
                    itemsRemoved++;
                }
            }
        });
        
        if (itemsRemoved > 0) {
            console.log(`Limpieza de caché: ${itemsRemoved} elementos obsoletos eliminados`);
        }
    } catch (error) {
        console.error('Error durante la limpieza de caché:', error);
    }
};

/**
 * Iniciar el servicio de limpieza automática
 */
export const initCacheCleanupService = () => {
    // Limpiar caché inmediatamente al cargar la aplicación
    cleanupStaleCache();
    
    // Programar limpieza cada 12 horas
    setInterval(cleanupStaleCache, 12 * 60 * 60 * 1000);
};

/**
 * Utilidades para limpiar caché y reiniciar estado de la aplicación
 */

/**
 * Limpia todo el caché de la aplicación
 */
export const clearAllCache = () => {
    try {
        // Limpiar caché de imágenes de perfil
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith('profile_picture_') || 
                key.startsWith('invoices') || 
                key.startsWith('billing_') ||
                key.startsWith('user_profile_') ||
                key.startsWith('user_contacts_')) {
                localStorage.removeItem(key);
            }
        });

        // Limpiar sessionStorage
        sessionStorage.clear();

        // Limpiar cookies específicas (mantener token y nit)
        const cookiesToKeep = ['token', 'nit', 'userRole'];
        const allCookies = document.cookie.split(';');
        allCookies.forEach(cookie => {
            const [name] = cookie.split('=');
            const cookieName = name.trim();
            if (!cookiesToKeep.includes(cookieName)) {
                document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
            }
        });

        console.log('✅ Caché limpiado exitosamente');
        return true;
    } catch (error) {
        console.error('❌ Error limpiando caché:', error);
        return false;
    }
};

/**
 * Limpia solo el caché de facturas
 */
export const clearBillingCache = () => {
    try {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith('invoices') || key.startsWith('billing_')) {
                localStorage.removeItem(key);
            }
        });
        console.log('✅ Caché de facturas limpiado');
        return true;
    } catch (error) {
        console.error('❌ Error limpiando caché de facturas:', error);
        return false;
    }
};

/**
 * Limpia solo el caché de imágenes de perfil
 */
export const clearProfilePictureCache = () => {
    try {
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith('profile_picture_')) {
                localStorage.removeItem(key);
            }
        });
        console.log('✅ Caché de imágenes de perfil limpiado');
        return true;
    } catch (error) {
        console.error('❌ Error limpiando caché de imágenes:', error);
        return false;
    }
};

/**
 * Reinicia el estado de la aplicación disparando eventos
 */
export const resetAppState = () => {
    try {
        // Disparar eventos para reiniciar contextos
        window.dispatchEvent(new CustomEvent('appStateReset', {
            detail: { timestamp: Date.now() }
        }));

        // Limpiar caché
        clearAllCache();

        // Recargar la página después de un breve retraso
        setTimeout(() => {
            window.location.reload();
        }, 1000);

        console.log('✅ Estado de aplicación reiniciado');
        return true;
    } catch (error) {
        console.error('❌ Error reiniciando estado:', error);
        return false;
    }
};

/**
 * Verifica el estado del caché
 */
export const checkCacheStatus = () => {
    try {
        const keys = Object.keys(localStorage);
        const cacheInfo = {
            totalKeys: keys.length,
            profilePictures: keys.filter(k => k.startsWith('profile_picture_')).length,
            invoices: keys.filter(k => k.startsWith('invoices')).length,
            billing: keys.filter(k => k.startsWith('billing_')).length,
            userData: keys.filter(k => k.startsWith('user_')).length
        };

        console.log('📊 Estado del caché:', cacheInfo);
        return cacheInfo;
    } catch (error) {
        console.error('❌ Error verificando caché:', error);
        return null;
    }
};

export default {
    clearAllCache,
    clearBillingCache,
    clearProfilePictureCache,
    resetAppState,
    checkCacheStatus
};
